import { useState, useEffect } from 'react';
import {
  getAllStudyAssociations,
  createStudyAssociation,
  updateStudyAssociation,
  deleteStudyAssociation,
  getAssociationUsers,
  moveUsersToAssociation,
  deleteUsers,
  uploadStudyAssociationLogo,
  deleteStudyAssociationLogo,
  type StudyAssociationDetailed,
  type AssociationUser,
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { Building2, Plus, Edit, Trash2, Users, Mail, Phone, MapPin, Calendar, Shield, AlertTriangle, Image } from 'lucide-react';
import LogoUpload from './LogoUpload';
import { BrregOrgLookup, type BrregLookupState } from './BrregOrgLookup';
import { useToast, useConfirm } from './ui';

export default function StudyAssociationManagement() {
  const currentUser = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [associations, setAssociations] = useState<StudyAssociationDetailed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState<StudyAssociationDetailed | null>(null);
  const [saving, setSaving] = useState(false);
  const [brregState, setBrregState] = useState<BrregLookupState>('idle');
  // Lagre-knapp blokkeres kun mens Brreg-oppslag pågår eller feiler.
  // 'found' (ekte Brreg eller testnr) + 'not_found' (bruker-ansvar) + 'idle' tillates.
  const brregBlocksSave = brregState === 'loading' || brregState === 'error';

  // Form fields
  const [name, setName] = useState('');
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');

  // Delete flow
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAssociation, setDeletingAssociation] = useState<StudyAssociationDetailed | null>(null);
  const [associationUsers, setAssociationUsers] = useState<AssociationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleteAction, setDeleteAction] = useState<'move' | 'delete' | null>(null);
  const [targetAssociationId, setTargetAssociationId] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadAssociations();
  }, []);

  const loadAssociations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllStudyAssociations();
      // Filter out "Kompetanseforbundet" (system owner organization) by ID
      // Test environment ID: a0000000-0000-0000-0000-000000000001
      // Prod environment ID: 007a5a29-a180-4e2c-8055-b4a17357b6a3
      const SYSTEM_OWNER_IDS = [
        'a0000000-0000-0000-0000-000000000001', // Test
        '007a5a29-a180-4e2c-8055-b4a17357b6a3'  // Prod
      ];
      const filteredData = data.filter(
        association => !SYSTEM_OWNER_IDS.includes(association.id)
      );
      setAssociations(filteredData);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste studieforbund');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    // Reset form
    setName('');
    setOrganizationNumber('');
    setContactEmail('');
    setContactPhone('');
    setAddress('');
    setShowCreateModal(true);
  };

  const handleEdit = (association: StudyAssociationDetailed) => {
    setEditingAssociation(association);
    setName(association.name);
    setOrganizationNumber(association.organization_number);
    setContactEmail(association.contact_email);
    setContactPhone(association.contact_phone || '');
    setAddress(association.address || '');
    setShowEditModal(true);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setSaving(true);
      await createStudyAssociation(currentUser.id, {
        name,
        organization_number: organizationNumber,
        contact_email: contactEmail,
        contact_phone: contactPhone || undefined,
        address: address || undefined,
      });

      setShowCreateModal(false);
      toast.showSuccess('Studieforbund opprettet!');
      await loadAssociations();
    } catch (err: any) {
      toast.showError(`Feil ved opprettelse: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !editingAssociation) return;

    try {
      setSaving(true);
      await updateStudyAssociation(currentUser.id, editingAssociation.id, {
        name: name !== editingAssociation.name ? name : undefined,
        organization_number:
          organizationNumber !== editingAssociation.organization_number
            ? organizationNumber
            : undefined,
        contact_email: contactEmail !== editingAssociation.contact_email ? contactEmail : undefined,
        contact_phone:
          contactPhone !== (editingAssociation.contact_phone || '') ? contactPhone : undefined,
        address: address !== (editingAssociation.address || '') ? address : undefined,
      });

      setShowEditModal(false);
      setEditingAssociation(null);
      toast.showSuccess('Studieforbund oppdatert!');
      await loadAssociations();
    } catch (err: any) {
      toast.showError(`Feil ved oppdatering: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInitiateDelete = async (association: StudyAssociationDetailed) => {
    if (!currentUser) return;

    setDeletingAssociation(association);
    setDeleteAction(null);
    setTargetAssociationId('');

    // Load users for this association
    try {
      setLoadingUsers(true);
      const users = await getAssociationUsers(currentUser.id, association.id);
      setAssociationUsers(users);
      setShowDeleteModal(true);
    } catch (err: any) {
      toast.showError(`Feil ved lasting av brukere: ${err.message}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteAssociation = async () => {
    if (!currentUser || !deletingAssociation) return;

    // If there are users, they must be handled first
    if (associationUsers.length > 0) {
      if (!deleteAction) {
        toast.showWarning('Vennligst velg hvordan brukerne skal håndteres');
        return;
      }

      if (deleteAction === 'move' && !targetAssociationId) {
        toast.showWarning('Vennligst velg et målstudieforbund');
        return;
      }

      try {
        setDeleting(true);

        // Handle users
        const userIds = associationUsers.map((u) => u.id);
        if (deleteAction === 'move') {
          await moveUsersToAssociation(currentUser.id, userIds, targetAssociationId);
        } else {
          await deleteUsers(currentUser.id, userIds);
        }

        // Now delete the association
        await deleteStudyAssociation(currentUser.id, deletingAssociation.id);

        toast.showSuccess(`Studieforbund "${deletingAssociation.name}" er slettet`);
        setShowDeleteModal(false);
        setDeletingAssociation(null);
        await loadAssociations();
      } catch (err: any) {
        toast.showError(`Feil ved sletting: ${err.message}`);
      } finally {
        setDeleting(false);
      }
    } else {
      // No users, delete directly — men fortsatt high-stakes (studieforbund er paraply over medlemsorganisasjoner)
      const confirmed = await confirm({
        title: 'Slett studieforbund',
        message: `Du er i ferd med å slette studieforbundet "${deletingAssociation.name}".\n\nDette er en permanent handling på høyeste organisasjonsnivå i systemet.`,
        confirmLabel: 'Ja, slett studieforbundet',
        cancelLabel: 'Avbryt',
        variant: 'high_stakes',
        requireInput: 'SLETT',
        inputPlaceholder: 'Skriv SLETT',
        consequences: [
          'Studieforbundet fjernes fra systemet og vil ikke lenger kunne være paraply for medlemsorganisasjoner.',
          'Alle historiske data (signerte DPA-er, godkjente studieplaner, utstedte attester) som refererer til dette studieforbundet beholdes for sporing.',
          'Dette studieforbundet har ingen aktive brukere eller medlemsorganisasjoner akkurat nå — ellers ville handlingen vært blokkert.',
          'Handlingen loggføres i audit_log og kan ikke angres uten manuell DB-intervensjon.'
        ]
      });

      if (!confirmed) return;

      try {
        setDeleting(true);
        await deleteStudyAssociation(currentUser.id, deletingAssociation.id);
        toast.showSuccess(`Studieforbund "${deletingAssociation.name}" er slettet`);
        setShowDeleteModal(false);
        setDeletingAssociation(null);
        await loadAssociations();
      } catch (err: any) {
        toast.showError(`Feil ved sletting: ${err.message}`);
      } finally {
        setDeleting(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-primary"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'systemadmin') {
    return (
      <div className="card text-center py-12">
        <Shield className="mx-auto mb-4 text-gray-400" size={64} />
        <h2 className="text-xl font-bold mb-2">Ingen tilgang</h2>
        <p className="text-gray-600">
          Du har ikke tilgang til studieforbund-administrasjon. Kun systemadministrator har tilgang.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Studieforbund-administrasjon</h1>
          <p className="mt-2 text-sm text-gray-700">
            Administrer studieforbund i systemet
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-kf-primary hover:bg-kf-secondary"
          >
            <Plus size={20} className="mr-2" />
            Opprett studieforbund
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Studieforbund</dt>
                  <dd className="text-lg font-medium text-gray-900">{associations.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-kf-primary-60" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Totalt antall brukere</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {associations.reduce((sum, a) => sum + Number(a.user_count), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Associations List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Studieforbund ({associations.length})
          </h2>
          <div className="space-y-4">
            {associations.length === 0 ? (
              <p className="text-sm text-gray-500">Ingen studieforbund</p>
            ) : (
              associations.map((association) => (
                <div
                  key={association.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        {association.logo_path ? (
                          <img
                            src={`${import.meta.env.VITE_API_URL || ''}${association.logo_path}`}
                            alt={association.name}
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <Building2 size={20} className="text-kf-primary" />
                        )}
                        <h3 className="text-lg font-medium text-gray-900">{association.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Aktiv
                        </span>
                        {association.logo_path && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-kf-blue-100 text-kf-blue-800">
                            <Image size={12} className="mr-1" />
                            Logo
                          </span>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <Building2 size={16} />
                          <span>Org.nr: {association.organization_number}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users size={16} />
                          <span>{association.user_count} brukere</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail size={16} />
                          <span>{association.contact_email}</span>
                        </div>
                        {association.contact_phone && (
                          <div className="flex items-center space-x-2">
                            <Phone size={16} />
                            <span>{association.contact_phone}</span>
                          </div>
                        )}
                        {association.address && (
                          <div className="flex items-center space-x-2">
                            <MapPin size={16} />
                            <span>{association.address}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Calendar size={16} />
                          <span>
                            Opprettet: {new Date(association.created_at).toLocaleDateString('no-NO')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(association)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        title="Rediger"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleInitiateDelete(association)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                        title="Slett"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>


      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">Opprett nytt studieforbund</h3>
            <form onSubmit={handleSubmitCreate} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label htmlFor="name" className="label">
                  Navn <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="organizationNumber" className="label">
                  Organisasjonsnummer <span className="text-red-500">*</span>
                </label>
                <input
                  id="organizationNumber"
                  type="text"
                  required
                  className="input"
                  value={organizationNumber}
                  onChange={(e) => setOrganizationNumber(e.target.value)}
                  placeholder="9 siffer — slår automatisk opp mot Brønnøysund"
                />
                <BrregOrgLookup
                  orgnr={organizationNumber}
                  currentName={name}
                  currentAddress={address}
                  scope="study_association"
                  onStateChange={setBrregState}
                  onApplyBrreg={(d) => {
                    setName(d.name);
                    if (d.address) setAddress(d.address);
                  }}
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="label">
                  Kontakt e-post <span className="text-red-500">*</span>
                </label>
                <input
                  id="contactEmail"
                  type="email"
                  required
                  className="input"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="contactPhone" className="label">
                  Kontakt telefon
                </label>
                <input
                  id="contactPhone"
                  type="tel"
                  className="input"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="address" className="label">
                  Adresse
                </label>
                <textarea
                  id="address"
                  className="input"
                  rows={3}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              </div>
              <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={saving || brregBlocksSave}
                  title={brregState === 'loading' ? 'Venter på Brønnøysund-oppslag…' : brregState === 'error' ? 'Brønnøysund-oppslag feilet — prøv igjen eller tøm organisasjonsnummer' : ''}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-kf-primary hover:bg-kf-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Oppretter...' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingAssociation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">Rediger studieforbund</h3>
            <form onSubmit={handleSubmitEdit} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label htmlFor="editName" className="label">
                  Navn <span className="text-red-500">*</span>
                </label>
                <input
                  id="editName"
                  type="text"
                  required
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editOrganizationNumber" className="label">
                  Organisasjonsnummer <span className="text-red-500">*</span>
                </label>
                <input
                  id="editOrganizationNumber"
                  type="text"
                  required
                  className="input"
                  value={organizationNumber}
                  onChange={(e) => setOrganizationNumber(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editContactEmail" className="label">
                  Kontakt e-post <span className="text-red-500">*</span>
                </label>
                <input
                  id="editContactEmail"
                  type="email"
                  required
                  className="input"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editContactPhone" className="label">
                  Kontakt telefon
                </label>
                <input
                  id="editContactPhone"
                  type="tel"
                  className="input"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editAddress" className="label">
                  Adresse
                </label>
                <textarea
                  id="editAddress"
                  className="input"
                  rows={3}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              {/* Logo Upload */}
              <LogoUpload
                currentLogoPath={editingAssociation.logo_path}
                onUpload={async (file) => {
                  const result = await uploadStudyAssociationLogo(editingAssociation.id, file);
                  if (result.success) {
                    // Update local state
                    setEditingAssociation({
                      ...editingAssociation,
                      logo_path: result.logo_path || null
                    });
                    // Refresh list
                    loadAssociations();
                  }
                  return result;
                }}
                onDelete={async () => {
                  await deleteStudyAssociationLogo(editingAssociation.id);
                  setEditingAssociation({
                    ...editingAssociation,
                    logo_path: null
                  });
                  loadAssociations();
                }}
                disabled={saving}
              />

              </div>
              <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAssociation(null);
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={saving || brregBlocksSave}
                  title={brregState === 'loading' ? 'Venter på Brønnøysund-oppslag…' : brregState === 'error' ? 'Brønnøysund-oppslag feilet — prøv igjen eller tøm organisasjonsnummer' : ''}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-kf-primary hover:bg-kf-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Lagrer...' : 'Lagre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingAssociation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-lg font-medium text-gray-900">Slett studieforbund</h3>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kf-primary"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Du er i ferd med å slette studieforbundet <strong>"{deletingAssociation.name}"</strong>.
                </p>

                {associationUsers.length > 0 ? (
                  <>
                    <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4">
                      <p className="text-sm font-medium text-kf-accent mb-2">
                        Det er {associationUsers.length} brukere tilknyttet dette studieforbundet.
                      </p>
                      <p className="text-sm text-kf-accent">
                        Du må velge hva som skal skje med disse brukerne før studieforbundet kan slettes.
                      </p>
                    </div>

                    {/* Users list */}
                    <div className="border border-gray-200 rounded max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Navn
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              E-post
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Rolle
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {associationUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-4 py-2 text-sm text-gray-900">{user.full_name}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{user.email}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">
                                {user.role === 'admin' ? 'Admin' : 'Bruker'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Action selection */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-900">Velg handling for brukerne:</p>

                      <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="deleteAction"
                          value="move"
                          checked={deleteAction === 'move'}
                          onChange={() => setDeleteAction('move')}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Flytt til et annet studieforbund</p>
                          <p className="text-xs text-gray-500">
                            Brukerne vil bli flyttet til et annet studieforbund og beholder sin rolle
                          </p>
                        </div>
                      </label>

                      {deleteAction === 'move' && (
                        <div className="ml-6">
                          <label className="label">Velg målstudieforbund</label>
                          <select
                            className="input"
                            value={targetAssociationId}
                            onChange={(e) => setTargetAssociationId(e.target.value)}
                          >
                            <option value="">-- Velg studieforbund --</option>
                            {associations
                              .filter((a) => a.id !== deletingAssociation.id)
                              .map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="deleteAction"
                          value="delete"
                          checked={deleteAction === 'delete'}
                          onChange={() => setDeleteAction('delete')}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Slett brukerne</p>
                          <p className="text-xs text-gray-500">
                            ADVARSEL: Brukerne vil bli permanent slettet fra systemet
                          </p>
                        </div>
                      </label>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded p-4">
                    <p className="text-sm text-gray-700">
                      Det er ingen brukere tilknyttet dette studieforbundet. Studieforbundet kan slettes direkte.
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletingAssociation(null);
                      setAssociationUsers([]);
                      setDeleteAction(null);
                      setTargetAssociationId('');
                    }}
                    disabled={deleting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAssociation}
                    disabled={deleting}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    {deleting ? 'Sletter...' : 'Slett studieforbund'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
